import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Anchor,
  Stack,
  Alert,
  Center,
  Box,
  Image,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get return URL from query params (e.g., from QR code scan)
  const returnUrl = searchParams.get('returnUrl') || '/dashboard';

  const form = useForm<LoginFormValues>({
    validate: zodResolver(loginSchema),
    initialValues: {
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setError(null);
    setIsLoading(true);

    console.log('[LOGIN] Starting login for:', values.email);

    try {
      const result = await login(values.email, values.password);

      console.log('[LOGIN] Login result:', result);

      if (result.success) {
        console.log('[LOGIN] Login successful, navigating to:', returnUrl);
        navigate(returnUrl);
      } else {
        console.error('[LOGIN] Login failed:', result.error);
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('[LOGIN] Exception during login:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
      console.log('[LOGIN] Login process complete');
    }
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={480}>
        <Center mb={40}>
          <Image src="/logo-inverse.png" alt="TrustPuma" h={90} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p={40} radius="lg">
          <Title order={2} ta="center" mb="md">
            Welcome back
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Sign in to your account to continue
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              <TextInput
                label="Email"
                placeholder="your@email.com"
                required
                size="md"
                {...form.getInputProps('email')}
              />

              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                size="md"
                {...form.getInputProps('password')}
              />

              <Group justify="apart">
                <Anchor component={Link} to="/forgot-password" size="sm">
                  Forgot password?
                </Anchor>
              </Group>

              <Button type="submit" fullWidth size="md" loading={isLoading}>
                Sign in
              </Button>
            </Stack>
          </form>

          <Stack gap="xs" mt="xl">
            <Text c="dimmed" size="sm" ta="center">
              Don't have an account?
            </Text>
            <Button
              component={Link}
              to="/register"
              variant="light"
              fullWidth
              size="md"
            >
              Start free trial
            </Button>
            <Text size="xs" c="dimmed" ta="center">No credit card required · 30 day trial</Text>
            <Group justify="center" gap="xs">
              <Anchor component={Link} to="/pricing" size="xs" c="dimmed">
                View all plans
              </Anchor>
              <Text size="xs" c="dimmed">·</Text>
              <Anchor href="https://www.trustpuma.com" size="xs" c="dimmed" target="_blank">
                Learn more
              </Anchor>
            </Group>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
